import { Link } from "react-router-dom";
import { useFirstOrg } from "../hooks/use-orgs.js";
import { useWebsites } from "../hooks/use-websites.js";
import {
  useSubscription,
  useCreatePortalSession,
  useBillingSummary,
  useUpcomingInvoice,
} from "../hooks/use-billing.js";
import { useFeatureReadiness } from "../hooks/use-feature-readiness.js";
import { PageSkeleton } from "../components/skeleton.js";

function StatusBadge({ status }: { status: string }) {
  const statusClass =
    {
      active: "status-badge--active",
      trialing: "status-badge--trialing",
      past_due: "status-badge--past-due",
      canceled: "status-badge--canceled",
      incomplete: "status-badge--incomplete",
    }[status] || "status-badge--default";

  const labelText =
    {
      trialing: "Trial Active",
      active: "Active",
      past_due: "Past Due",
      canceled: "Canceled",
      incomplete: "Incomplete",
    }[status] || status;

  return <span className={`status-badge ${statusClass}`}>{labelText}</span>;
}

function UsageBar({
  used,
  limit,
  label,
}: {
  used: number;
  limit: number;
  label: string;
}) {
  const pct =
    limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const fillClass =
    pct >= 90
      ? "usage-bar__fill--danger"
      : pct >= 70
        ? "usage-bar__fill--warning"
        : "";

  return (
    <div className="usage-bar">
      <div className="usage-bar__header">
        <span>{label}</span>
        <span>
          {used} / {limit}
        </span>
      </div>
      <div className="usage-bar__track" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${label}: ${used} of ${limit} used`}>
        <div
          className={`usage-bar__fill ${fillClass}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function FeatureItem({
  enabled,
  label,
}: {
  enabled: boolean;
  label: string;
}) {
  const className = enabled
    ? "feature-list__item feature--enabled"
    : "feature-list__item feature--disabled";

  return (
    <li className={className}>
      <span className="feature-icon">{enabled ? "\u2713" : "\u00d7"}</span>
      <span>{label}</span>
    </li>
  );
}

export function BillingPage() {
  const org = useFirstOrg();
  const { data: websitesData } = useWebsites(org?.id);
  const firstWebsiteId = websitesData?.websites[0]?.id;
  const { data, isLoading, isError: subError } = useSubscription(org?.id);
  const { data: summaryData, isLoading: summaryLoading } = useBillingSummary(
    org?.id,
  );
  const { data: invoiceData } = useUpcomingInvoice(org?.id);
  const portal = useCreatePortalSession();
  const { data: features } = useFeatureReadiness();

  const billingDegraded = features?.billing?.status === "degraded";

  const sub = data?.subscription;
  const summary = summaryData?.summary;
  const invoice = invoiceData?.invoice;

  function handlePortal() {
    if (!org) return;
    portal.mutate(
      { orgId: org.id },
      {
        onSuccess(result) {
          if (result.portalUrl?.startsWith("https://")) {
            window.location.href = result.portalUrl;
          }
        },
      },
    );
  }

  const daysUntilTrialEnd =
    sub?.trialEndsAt && sub.status === "trialing"
      ? Math.ceil(
          (new Date(sub.trialEndsAt).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24),
        )
      : null;

  // Total scans allowed across all websites
  const totalScansAllowed = summary
    ? summary.limits.scansPerMonthPerWebsite * summary.limits.maxWebsites
    : 0;

  return (
    <div className="billing-page">
      <div className="billing-page__header">
        <h1 className="page-title">Billing</h1>
        <p className="page-subtitle">
          Manage your subscription and payment method.
        </p>
      </div>

      {isLoading && <PageSkeleton />}

      {subError && !isLoading && (
        <div className="alert alert--error mb-3" role="alert">
          Unable to load your subscription details. Please refresh the page or
          try again later.
        </div>
      )}

      {billingDegraded && (
        <div className="alert alert--error mb-3" role="alert">
          <strong>Billing is not fully configured.</strong> The payment webhook
          is missing, which means subscription changes may not sync correctly.
          Do not make billing changes until this is resolved by an
          administrator.
        </div>
      )}

      {/* ── Free plan (no subscription record) ─────────────────────────── */}
      {!isLoading && !subError && !sub && (
        <>
          <section className="product-section">
            <div className="stat-grid">
              <div className="stat-item">
                <span className="stat-item__value">Free</span>
                <span className="stat-item__label">Current Plan</span>
              </div>
              <div className="stat-item">
                <span className="stat-item__value">
                  <StatusBadge status="active" />
                </span>
                <span className="stat-item__label">Status</span>
              </div>
              <div className="stat-item">
                <span className="stat-item__value">&euro;0.00/mo</span>
                <span className="stat-item__label">Price</span>
              </div>
            </div>
            <p className="text-muted text-sm mt-3">
              You are on the Free plan &mdash; 1 website, 10 pages per scan, 1
              scan per month. Upgrade for deeper scanning, more websites, and
              advanced features.
            </p>
          </section>

          {/* Upgrade action for free plan */}
          <section className="product-section">
            <h2 className="section-title">Upgrade your plan</h2>
            <p className="text-muted text-sm mb-3">
              Unlock more websites, deeper scans, document scanning, and
              regression detection by upgrading to a paid plan.
            </p>
            {firstWebsiteId ? (
              <Link
                to={`/wizard/${firstWebsiteId}/plan`}
                className="btn btn--primary"
              >
                View plans and upgrade
              </Link>
            ) : (
              <Link to="/setup/website" className="btn btn--primary">
                Add a website to get started
              </Link>
            )}
          </section>
        </>
      )}

      {/* ── Subscription exists ─────────────────────────────────────────── */}
      {!isLoading && sub && (
        <>
          {/* Plan status */}
          <section
            className="product-section"
            aria-labelledby="plan-heading"
          >
            <h2 id="plan-heading" className="sr-only">
              Plan status
            </h2>
            <div className="stat-grid">
              <div className="stat-item">
                <span className="stat-item__value">
                  {sub.plan?.name ?? sub.planId}
                </span>
                <span className="stat-item__label">Current Plan</span>
              </div>
              <div className="stat-item">
                <span className="stat-item__value">
                  <StatusBadge status={sub.status} />
                </span>
                <span className="stat-item__label">Status</span>
              </div>
              {sub.plan && sub.plan.priceEurCents > 0 && (
                <div className="stat-item">
                  <span className="stat-item__value">
                    &euro;{(sub.plan.priceEurCents / 100).toFixed(2)}/mo
                  </span>
                  <span className="stat-item__label">Price</span>
                </div>
              )}
              {sub.plan && sub.plan.priceEurCents === 0 && (
                <div className="stat-item">
                  <span className="stat-item__value">Free</span>
                  <span className="stat-item__label">Price</span>
                </div>
              )}
              {sub.trialEndsAt && sub.status === "trialing" && (
                <div className="stat-item">
                  <span className="stat-item__value">
                    {new Date(sub.trialEndsAt).toLocaleDateString("en-IE")}
                  </span>
                  <span className="stat-item__label">Trial Ends</span>
                </div>
              )}
              {sub.currentPeriodEnd && sub.status !== "trialing" && (
                <div className="stat-item">
                  <span className="stat-item__value">
                    {new Date(sub.currentPeriodEnd).toLocaleDateString(
                      "en-IE",
                    )}
                  </span>
                  <span className="stat-item__label">Renewal Date</span>
                </div>
              )}
            </div>
          </section>

          {/* Trial info */}
          {sub.status === "trialing" && daysUntilTrialEnd !== null && (
            <section className="product-section trial-info-box">
              <h2 className="section-title">Trial Status</h2>
              <p>
                Your trial ends{" "}
                <strong>
                  {new Date(sub.trialEndsAt!).toLocaleDateString("en-IE")}
                </strong>{" "}
                (
                {daysUntilTrialEnd > 0
                  ? `${daysUntilTrialEnd} days left`
                  : "today"}
                )
              </p>
              {sub.plan && sub.plan.priceEurCents > 0 && (
                <p>
                  After that, you'll be charged{" "}
                  <strong>
                    &euro;{(sub.plan.priceEurCents / 100).toFixed(2)}/month
                  </strong>
                  .
                </p>
              )}
              <p className="text-muted text-sm mt-2">
                You can cancel anytime before the first charge with no
                penalty.
              </p>
            </section>
          )}

          {/* Upcoming invoice */}
          {invoice && sub.status !== "trialing" && (
            <section className="product-section">
              <h2 className="section-title">Next Invoice</h2>
              <div className="invoice-card">
                <div className="invoice-card__row">
                  <div>
                    <span className="invoice-card__label">Amount due</span>
                    <span className="invoice-card__value">
                      &euro;{(invoice.amountDueEurCents / 100).toFixed(2)}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="invoice-card__label">Due on</span>
                    <span className="invoice-card__date-value">
                      {new Date(invoice.periodEnd).toLocaleDateString(
                        "en-IE",
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Usage and limits */}
          {!summaryLoading && summary && (
            <section
              className="product-section"
              aria-labelledby="limits-heading"
            >
              <h2 id="limits-heading" className="section-title">
                Usage and Limits
              </h2>

              <div className="limits-grid">
                <div className="limit-card">
                  <h3>Websites</h3>
                  <div className="limit-card__hero">
                    {summary.limits.maxWebsites}
                  </div>
                  <p className="limit-desc">
                    {summary.limits.maxWebsites === 1
                      ? "1 website allowed"
                      : `Up to ${summary.limits.maxWebsites} websites`}
                  </p>
                </div>

                <div className="limit-card">
                  <h3>Scans per Month</h3>
                  <div className="limit-card__hero">
                    {totalScansAllowed}
                  </div>
                  <UsageBar
                    used={summary.usage.scansUsed}
                    limit={totalScansAllowed}
                    label="Used this month"
                  />
                </div>

                <div className="limit-card">
                  <h3>Pages per Scan</h3>
                  <div className="limit-card__hero">
                    {summary.limits.maxPagesPerScan}
                  </div>
                  <p className="limit-desc">
                    Maximum pages scanned per run
                  </p>
                </div>

                <div className="limit-card">
                  <h3>Scan History</h3>
                  <div className="limit-card__hero">
                    {summary.limits.scanHistoryMonths > 0
                      ? summary.limits.scanHistoryMonths
                      : "N/A"}
                  </div>
                  <p className="limit-desc">
                    {summary.limits.scanHistoryMonths > 0
                      ? `${summary.limits.scanHistoryMonths} month${summary.limits.scanHistoryMonths !== 1 ? "s" : ""} of history`
                      : "Upgrade for scan history retention"}
                  </p>
                </div>

                {summary.usage.documentsScanned > 0 && (
                  <div className="limit-card">
                    <h3>Documents Scanned</h3>
                    <div className="limit-card__hero">
                      {summary.usage.documentsScanned}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-3">
                <h3 className="section-title">Included Features</h3>
                <ul className="feature-list">
                  <FeatureItem
                    enabled={summary.limits.docsEnabled}
                    label="Document scanning"
                  />
                  <FeatureItem
                    enabled={summary.limits.recurringEnabled}
                    label="Regression detection"
                  />
                </ul>
              </div>
            </section>
          )}

          {/* Manage subscription */}
          {sub.status !== "canceled" && (
            <section className="product-section">
              <h2 className="section-title">Manage subscription</h2>
              {sub.hasPaymentMethod ? (
                <>
                  <p className="text-muted text-sm mb-3">
                    Use the billing portal to update your payment method, change
                    your plan, or cancel your subscription. You will be
                    redirected to our payment provider (Stripe).
                  </p>
                  <div className="billing-actions">
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={() => void handlePortal()}
                      disabled={portal.isPending || billingDegraded}
                    >
                      {portal.isPending ? "Loading\u2026" : "Manage billing"}
                    </button>
                  </div>
                  {billingDegraded && (
                    <p className="text-warning text-sm mt-2">
                      Billing portal disabled &mdash; webhook not configured.
                    </p>
                  )}
                  {portal.isError && (
                    <div className="alert alert--error mt-3" role="alert">
                      Could not open the billing portal. Please try again or
                      contact support.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="text-muted text-sm mb-3">
                    No payment method on file yet. Add a payment method to
                    continue after your trial ends.
                  </p>
                  {firstWebsiteId && (
                    <Link
                      to={`/wizard/${firstWebsiteId}/plan`}
                      className="btn btn--primary"
                    >
                      Add payment method
                    </Link>
                  )}
                </>
              )}
            </section>
          )}

          {/* Canceled subscription */}
          {sub.status === "canceled" && firstWebsiteId && (
            <section className="product-section">
              <h2 className="section-title">Reactivate your subscription</h2>
              <p className="text-muted text-sm mb-3">
                Your subscription has been canceled. You can reactivate by
                choosing a new plan.
              </p>
              <Link
                to={`/wizard/${firstWebsiteId}/plan`}
                className="btn btn--primary"
              >
                Choose a plan
              </Link>
            </section>
          )}
        </>
      )}
    </div>
  );
}
